import { useOrganization, useUser } from "@clerk/tanstack-react-start"
import { api } from "@stoneflow/backend/convex/_generated/api"
import type { FeatureFlag } from "@stoneflow/shared"
import { useConvexAuth, useQuery } from "convex/react"
import { createContext, useCallback, useContext } from "react"
import { parseRoles } from "#/lib/roles"

interface FeatureFlagContextValue {
	isLoaded: boolean
	hasFlag: (flagName: FeatureFlag) => boolean
}

const FeatureFlagContext = createContext<FeatureFlagContextValue>({
	isLoaded: false,
	hasFlag: () => false,
})

export function FeatureFlagProvider({ children }: { children: React.ReactNode }) {
	const { isAuthenticated } = useConvexAuth()
	const { user } = useUser()
	const { organization } = useOrganization()

	const flags = useQuery(api.platform.featureFlags.getAll, isAuthenticated ? {} : "skip")

	const hasFlag = useCallback(
		(flagName: FeatureFlag): boolean => {
			if (!flags || !user) return false

			const flag = flags.find((f) => f.name === flagName)
			if (!flag || !flag.enabled) return false

			const rules = flag.targetingRules
			if (!rules) return true

			if (rules.userClerkIds?.includes(user.id)) return true

			const userRoles = parseRoles(user.publicMetadata)
			if (rules.roles?.some((role) => userRoles.includes(role as (typeof userRoles)[number])))
				return true

			if (organization && rules.orgIds?.includes(organization.id)) return true

			return false
		},
		[flags, user, organization],
	)

	return (
		<FeatureFlagContext.Provider value={{ isLoaded: flags !== undefined, hasFlag }}>
			{children}
		</FeatureFlagContext.Provider>
	)
}

export function useFeatureFlags() {
	return useContext(FeatureFlagContext)
}

export function useFeatureFlag(flagName: FeatureFlag): boolean {
	return useFeatureFlags().hasFlag(flagName)
}
